/**
 * MeasureTool — the ruler (#155): drag between two points to read the distance
 * between them.
 *
 * BOTH gestures work: drag from one point to the other, or click the start and
 * click again at the end. They are the same measurement — the release decides
 * which one you made, by whether the pointer travelled. Two-click is what a
 * long measurement across a zoomed-in canvas wants (you can pan between the
 * clicks); drag is what a quick one wants.
 *
 * A top-level tool rather than a Construction or Leather sub-tool, because it
 * belongs to neither: you measure whatever is on the canvas, and a ruler that
 * only worked while you happened to be in the right mode would be a ruler you
 * had to put down to use.
 *
 * It COMMITS NOTHING. A measurement is a question, not an edit — it leaves no
 * trace in the model, doesn't enter the undo history, and survives only until
 * the next one or until you leave the tool. That is also why it can snap
 * freely: nothing downstream has to live with where the point landed.
 *
 * Snapping is the shared draw snap (`resolveDrawSnap`), so the ruler lands on
 * the same vertices, edges, crossings and grid intersections the drawing tools
 * do, and obeys the same Snap toggles. Measuring corner-to-corner is the
 * common case, and a ruler that missed the corner by a pixel would be worse
 * than no ruler.
 *
 * The snap is shown BEFORE the press, not just after: hovering marks the point
 * the measurement would start from, using the same ring the drawing tools show
 * while snapping. Without it the start snap is invisible — it fires, but you
 * can't see that it will, so the only way to find out is to take a measurement
 * and check whether the number looks right. An affordance you have to commit to
 * a gesture to discover is one most people conclude isn't there.
 *
 * ANGLE snap holds the measurement to a drafting angle (the shared
 * `snapToCommonAngle`, on the Angle toggle or the modifier key) — "how far is
 * it straight up from here" is a question a ruler is asked constantly, and
 * without it the answer is off by however much the hand drifted. Landing on
 * something wins over holding an angle when both could fire: a vertex under
 * the cursor is a specific thing the user is pointing AT, where an angle is
 * only a direction. When angle and LINE snap are both on they combine instead
 * of competing: the end lands where the held direction crosses the nearest
 * edge, which is "along this way, as far as that" — the measurement the pair
 * of them is actually for. Construction's `angleLineEnd` (#323) does the same
 * for a drawn line.
 */
import type { PointerSample } from "../canvas/PointerInput";
import type { Vec2 } from "../model/Project";
import { ToolBase } from "./ToolBase";
import { setCanvasCursor } from "../canvas/canvasCursor";
import type paper from "paper";
import {
  drawSnapRadiusMm,
  gatherDrawSnapTargets,
  resolveDrawSnap,
} from "./drawSnap";
import {
  angleModifierHeld,
  isAngleSnapActive,
  isLineSnapActive,
  snapToCommonAngle,
} from "./dragSnap";
import { setAngleHint } from "../snapAngleIndicator";

/** Below this (screen px) a press-release is a CLICK, not a drag. A click
 *  starts the two-click gesture instead of finishing a zero-length drag. */
const DRAG_SLOP_PX = 4;
/** Below this (project mm) a finished measurement is degenerate — the two ends
 *  are the same point, so there is nothing to read. Cleared rather than left as
 *  a mark on the canvas. */
const MIN_MEASURE_MM = 0.01;

export class MeasureTool extends ToolBase {
  id = "measure";
  label = "Measure";
  cursor = "crosshair";

  /** Where the current drag started, snapped. Null between measurements. */
  private from: Vec2 | null = null;
  /** Whether that start point landed ON something — carried through the
   *  gesture so its ring stays up while the far end moves. */
  private fromSnapped = false;
  /** Two-click gesture: the start is placed and the next click ends it. False
   *  during (and after) a drag. */
  private armed = false;
  /** Raw press position, for telling a click from a drag on release. */
  private pressedAt: Vec2 | null = null;

  /** Snap a point to whatever is nearby — vertices, edges, crossings, grid —
   *  per the Snap toggles. `hit` is null when nothing was near enough. */
  private snapTargets() {
    return gatherDrawSnapTargets(
      this.ctx.history.state,
      this.ctx.scene.getPaperScope(),
      this.ctx.scene.getPartLayoutView(),
    );
  }

  private snapToGeometry(p: { x: number; y: number }) {
    return resolveDrawSnap(this.ctx, { x: p.x, y: p.y }, this.snapTargets());
  }

  /** The far end of the measurement: geometry snap first, angle snap second.
   *  Both can be armed at once, and landing ON something beats holding a
   *  direction — the vertex is a thing the user is pointing at. */
  private snapEnd(
    p: { x: number; y: number },
    s: PointerSample,
  ): { point: Vec2; snapped: boolean } {
    const targets = this.snapTargets();
    const geometry = resolveDrawSnap(this.ctx, { x: p.x, y: p.y }, targets);
    // A VERTEX wins outright, angle or no angle: it is a specific point the
    // user is aiming at, and there is nothing an angle could add to it. Same
    // order construction's draw uses.
    if (geometry.hit?.kind === "vertex") {
      setAngleHint(null);
      return { point: geometry.point, snapped: true };
    }
    if (
      this.from &&
      isAngleSnapActive(this.ctx.settings, angleModifierHeld(s))
    ) {
      const sn = snapToCommonAngle(this.from, { x: p.x, y: p.y });
      setAngleHint(sn.angleRad);
      // Angle AND line together: land where the held direction CROSSES a line,
      // which satisfies both rather than making them compete. Discarding the
      // angle the moment a line came within reach was the wrong trade — the
      // angle is the thing the user is holding, and "along this direction as
      // far as that edge" is the measurement they're after. Construction's
      // `angleLineEnd` (#323) does exactly this for a drawn line.
      const crossing = this.angleLineEnd(
        this.from,
        { x: sn.x, y: sn.y },
        targets.edges,
      );
      if (crossing) return { point: crossing, snapped: true };
      // No line in reach: held to a direction only. No ring — the point isn't
      // pinned to a place, and the angle chip is what reports the direction.
      return { point: { x: sn.x, y: sn.y }, snapped: false };
    }
    setAngleHint(null);
    return { point: geometry.point, snapped: geometry.hit !== null };
  }

  /** Where the ray through `anglePt` from `origin` crosses the nearest edge,
   *  within the pull radius of that point — or null when line snap is off or
   *  nothing is in reach. The measurement then keeps the exact angle AND ends
   *  on real geometry. Mirrors `ConstructionSnapController.angleLineEnd`, over
   *  the edges this tool already snaps to (leather outlines included) rather
   *  than construction lines alone. */
  private angleLineEnd(
    origin: Vec2,
    anglePt: Vec2,
    edges: ReadonlyArray<string>,
  ): Vec2 | null {
    if (!isLineSnapActive(this.ctx.settings)) return null;
    const dx = anglePt.x - origin.x;
    const dy = anglePt.y - origin.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6 || edges.length === 0) return null;
    const scope = this.ctx.scene.getPaperScope();
    const ux = dx / len;
    const uy = dy / len;
    const BIG = 100000; // mm — past any cutting mat, in both directions
    let ray: paper.Path;
    try {
      ray = new scope.Path.Line({
        from: new scope.Point(origin.x - ux * BIG, origin.y - uy * BIG),
        to: new scope.Point(origin.x + ux * BIG, origin.y + uy * BIG),
        insert: false,
      });
    } catch {
      return null;
    }
    const at = new scope.Point(anglePt.x, anglePt.y);
    let best: Vec2 | null = null;
    let bestDist = drawSnapRadiusMm(scope);
    for (const pathData of edges) {
      let cp: paper.CompoundPath;
      try {
        cp = new scope.CompoundPath({ pathData, insert: false });
      } catch {
        continue;
      }
      try {
        for (const it of ray.getIntersections(cp)) {
          const d = it.point.getDistance(at);
          if (d <= bestDist) {
            bestDist = d;
            best = { x: it.point.x, y: it.point.y };
          }
        }
      } catch {
        /* an unparseable edge just doesn't contribute a crossing */
      }
      cp.remove();
    }
    ray.remove();
    return best;
  }

  override onActivate(): void {
    setCanvasCursor(this.ctx.scene.getPaperScope(), this.cursor);
  }

  override onHover(s: PointerSample, p: { x: number; y: number }): void {
    // Waiting for the second click: the measurement follows the cursor, so the
    // two-click gesture reads exactly like the drag does.
    if (this.armed && this.from) {
      const end = this.snapEnd(p, s);
      this.ctx.scene.setMeasurement({
        a: this.from,
        b: end.point,
        aSnapped: this.fromSnapped,
        bSnapped: end.snapped,
      });
      this.ctx.scene.setDrawSnapPoint(null);
      return;
    }
    // Otherwise mark where a press would land, so the start snap is visible
    // before you commit to it. The measurement's own rings take over once the
    // gesture begins, so this one is cleared then — two markers on one point
    // would be one too many.
    const snapped = this.snapToGeometry(p);
    this.ctx.scene.setDrawSnapPoint(snapped.hit ? snapped.point : null);
  }

  override onDeactivate(): void {
    this.from = null;
    this.fromSnapped = false;
    this.armed = false;
    this.pressedAt = null;
    setAngleHint(null);
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.setMeasurement(null);
    setCanvasCursor(this.ctx.scene.getPaperScope(), "default");
  }

  override onDown(s: PointerSample, p: { x: number; y: number }): void {
    this.ctx.scene.setDrawSnapPoint(null);
    // Second click of the two-click gesture: this press ENDS the measurement.
    if (this.armed && this.from) {
      this.finish(this.snapEnd(p, s));
      return;
    }
    // A new start replaces the last measurement rather than adding to it —
    // one ruler, moved around, the way a physical one works. Only geometry
    // snap applies to the START: there is no direction to hold yet.
    const start = this.snapToGeometry(p);
    this.from = start.point;
    this.fromSnapped = start.hit !== null;
    this.pressedAt = { x: p.x, y: p.y };
    this.ctx.scene.setMeasurement({
      a: this.from,
      b: this.from,
      aSnapped: this.fromSnapped,
      bSnapped: this.fromSnapped,
    });
  }

  override onMove(s: PointerSample, p: { x: number; y: number }): void {
    if (!this.from || this.armed) return;
    const end = this.snapEnd(p, s);
    this.ctx.scene.setMeasurement({
      a: this.from,
      b: end.point,
      aSnapped: this.fromSnapped,
      bSnapped: end.snapped,
    });
  }

  override onUp(s: PointerSample, p: { x: number; y: number }): void {
    if (!this.from || this.armed) return;
    // Which gesture was this? A release that never travelled is a CLICK, and
    // arms the two-click gesture rather than finishing a zero-length drag.
    // Measured in SCREEN px so the slop feels the same at any zoom.
    const zoom = this.ctx.scene.getPaperScope().view.zoom || 1;
    const start = this.pressedAt ?? this.from;
    const travelled = Math.hypot(p.x - start.x, p.y - start.y) * zoom;
    if (travelled < DRAG_SLOP_PX) {
      this.armed = true;
      setAngleHint(null);
      return;
    }
    this.finish(this.snapEnd(p, s));
  }

  /** Land the far end and put the gesture away. The measurement STAYS on
   *  screen — you took it in order to read it, and it would be gone before you
   *  looked up otherwise. The rings stay with it: they are part of reading the
   *  number, not part of the gesture. */
  private finish(end: { point: Vec2; snapped: boolean }): void {
    if (!this.from) return;
    const len = Math.hypot(
      end.point.x - this.from.x,
      end.point.y - this.from.y,
    );
    if (len < MIN_MEASURE_MM) this.ctx.scene.setMeasurement(null);
    else
      this.ctx.scene.setMeasurement({
        a: this.from,
        b: end.point,
        aSnapped: this.fromSnapped,
        bSnapped: end.snapped,
      });
    this.from = null;
    this.fromSnapped = false;
    this.armed = false;
    this.pressedAt = null;
    // The chip reports an angle being HELD, which stops being true once the
    // measurement is finished.
    setAngleHint(null);
  }

  override onCancel(): void {
    this.from = null;
    this.fromSnapped = false;
    this.armed = false;
    this.pressedAt = null;
    setAngleHint(null);
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.setMeasurement(null);
  }
}
